import { useClientPortalAPI } from "@certn/api";
import {
  Container,
  Layout,
  Loader,
  SecondaryTopNav,
  SectionMessage,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from "@certn/cascadia-react";
import { Trans, useTranslation } from "react-i18next";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useResolvedPath,
} from "react-router-dom";
import { createIncludesTabIdMapper } from "@certn/utils";
import { useFlags } from "launchdarkly-react-client-sdk";

import { UserDetailsHeadingCard } from "./components/user-details/UserDetailsHeadingCard";
import { UserDetailsTab } from "./components/user-details/UserDetailsTab";
import { UserGroupsTab } from "./components/user-details/UserGroupsTab";
import { UserPermissionsTab } from "./components/user-details/permissions/UserPermissionsTab";
import { PageTitle } from "../../components/page-title/PageTitle";

export function UserDetails() {
  const { pathname } = useLocation();
  const url = useResolvedPath("").pathname;

  const mapSelectedTabId = createIncludesTabIdMapper(
    [
      { substring: "/groups", id: "groups-tab" },
      { substring: "/permissions", id: "permissions-tab" },
    ],
    "details-tab"
  );

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId } = useParams();
  const { clientPortalHidePricingFromUser } = useFlags();
  const {
    data: user,
    mutate: refreshUser,
    isLoading: isUserLoading,
  } = useClientPortalAPI(`/api/internal/client-portal/users/${userId}/`);
  const { data: groups } = useClientPortalAPI(
    `/api/internal/client-portal/users/${userId}/customer-groups/`
  );

  return (
    <Layout
      layout="narrow"
      header={
        <SecondaryTopNav
          testID="back-button"
          leftIcon="arrow_back"
          onleftIconOnPress={() => {
            navigate("/settings/users");
          }}
          titleArea={{
            title: t("User management"),
          }}
        />
      }
    >
      <Container
        padding={["space-3"]}
        layout="applicant/narrow"
        testID="user-details-page-container"
      >
        {isUserLoading && <Loader overlay background="background-base" />}
        <PageTitle>
          <Trans>User details</Trans>
        </PageTitle>
        <UserDetailsHeadingCard
          name={user?.name || "--"}
          email={user?.email || ""}
          isActive={!user?.is_auth0_blocked}
          refreshUser={refreshUser}
        />
        {groups?.customer_groups?.length === 0 && (
          <SectionMessage
            sectionTitle={t("No groups assigned")}
            icon="warning"
            type="warning"
          >
            <Trans>This user currently has no assigned groups.</Trans>
          </SectionMessage>
        )}
        <Tabs
          selectedKey={mapSelectedTabId(pathname)}
          aria-label="user-details-tabs"
          testID="user-details-tabs"
        >
          <TabList aria-label="Tabs">
            <Tab id="details-tab" href={`${url}`} testID="user-details-tab">
              <Trans>Details</Trans>
            </Tab>
            <Tab
              id="groups-tab"
              href={`${url}/groups`}
              testID="user-details-groups-tab"
            >
              <Trans>Groups</Trans>
            </Tab>
            {clientPortalHidePricingFromUser && (
              <Tab
                id="permissions-tab"
                href={`${url}/permissions`}
                testID="user-details-permissions-tab"
              >
                <Trans>Permissions</Trans>
              </Tab>
            )}
          </TabList>
          <TabPanel id={mapSelectedTabId(pathname)}>
            <Routes>
              <Route path="" element={<UserDetailsTab />} />
              <Route path="/groups" element={<UserGroupsTab />} />
              {clientPortalHidePricingFromUser && (
                <Route path="/permissions" element={<UserPermissionsTab />} />
              )}
            </Routes>
          </TabPanel>
        </Tabs>
      </Container>
    </Layout>
  );
}
